/**
 * File System Access API helpers.
 *
 * Chromium-only progressive enhancement: when available, "Save" writes back
 * to the user-selected file without re-prompting. Other browsers fall back
 * to the download path via `triggerDownload`.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    showSaveFilePicker?: (opts?: any) => Promise<FileSystemFileHandle>;
    showOpenFilePicker?: (opts?: any) => Promise<FileSystemFileHandle[]>;
  }
}

let fileHandle: FileSystemFileHandle | null = null;

export function getFileHandle(): FileSystemFileHandle | null {
  return fileHandle;
}

export function setFileHandle(handle: FileSystemFileHandle | null): void {
  fileHandle = handle;
}

export function hasFileSystemAccess(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.showSaveFilePicker === "function"
  );
}

/** Trigger a browser download of a Blob with the given filename. */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Save via the File System Access API. If `existingHandle` is provided,
 * writes to it directly; otherwise prompts the user. Returns the handle on
 * success, or `null` if the user cancelled the picker.
 */
export async function saveWithFileSystemAccess(
  blob: Blob,
  existingHandle: FileSystemFileHandle | null,
  suggestedName: string,
): Promise<FileSystemFileHandle | null> {
  try {
    const handle =
      existingHandle ??
      (await window.showSaveFilePicker!({
        suggestedName,
        types: [
          {
            description: "PoseRef Scene",
            accept: { "application/json": [".pr"] },
          },
        ],
      }));
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return handle;
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return null;
    throw e;
  }
}
